import React, { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";

import ChartDataLabels from "chartjs-plugin-datalabels";
import { Search } from "react-bootstrap-icons";
import { RootState } from "../../redux/reducers";
import { connect } from "react-redux";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

export interface UbicacionChartItem {
    label: string;
    value: number;
    percentage?: number;
    depCorr: number;
}

interface Props {
    data: UbicacionChartItem[];
    title?: string;
    horizontal?: boolean;
    itemsPerPage?: number;

    /** Altura visible del gráfico en px */
    containerHeight?: number;

    /** Altura de cada barra en px */
    itemHeight?: number;

    onDependenciaClick?: (depCorr: number, anio?: number) => void;
    isDarkMode: boolean;
}

const UbicacionBarChart: React.FC<Props> = ({ data, title = "Bienes por Ubicación", horizontal = true, itemsPerPage = 10, containerHeight = 300,
    itemHeight = 50,
    onDependenciaClick,
    isDarkMode
}) => {

    const [startIndex, setStartIndex] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");

    const normalizeText = (text: string): string => {
        return text
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .trim();
    };

    const filteredData = useMemo(() => {
        const search = normalizeText(searchTerm);

        if (!search) {
            return data;
        }

        return data.filter((item) =>
            normalizeText(item.label).includes(search)
        );
    }, [data, searchTerm]);

    const visibleItems = Math.max(
        1,
        Math.min(itemsPerPage, filteredData.length || 1)
    );

    useEffect(() => {
        setStartIndex(0);
    }, [data, searchTerm]);

    const visibleData = filteredData.slice(startIndex,
        startIndex + visibleItems
    );

    const chartData = {
        labels: visibleData.map((item) => item.label),

        datasets: [
            {
                label: title,
                data: visibleData.map((item) => item.value),
                backgroundColor: "#0B5FA5",
                borderColor: "#004485",
                borderWidth: 1,
                borderRadius: 5,
            },
        ],
    };

    const totalHeight = Math.max(
        containerHeight,
        filteredData.length * itemHeight
    );

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const element = event.currentTarget;
        const scrollTop = element.scrollTop;

        const newStartIndex = Math.floor(
            scrollTop / itemHeight
        );

        const maxStartIndex = Math.max(
            0,
            filteredData.length - visibleItems
        );

        const finalIndex = Math.min(
            newStartIndex,
            maxStartIndex
        );

        if (finalIndex !== startIndex) {
            setStartIndex(finalIndex);
        }
    };

    const handleBarClick = (_event: any, elements: any[]) => {
        if (!elements || elements.length === 0) {
            return;
        }

        const index = elements[0].index;

        const dependenciaSeleccionada = visibleData[index];

        if (!dependenciaSeleccionada) {
            return;
        }

        if (onDependenciaClick) {
            onDependenciaClick(
                dependenciaSeleccionada.depCorr
            );
        }
    };

    const barOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {} as const,

        indexAxis: horizontal
            ? ("y" as const)
            : ("x" as const),

        onClick: handleBarClick,

        onHover: (event: any, elements: any[]) => {
            const target = event?.native?.target;

            if (target) {
                target.style.cursor =
                    elements.length > 0
                        ? "pointer"
                        : "default";
            }
        },

        plugins: {

            legend: {
                display: false,
            },

            tooltip: {
                callbacks: {
                    label: function (context: any) {
                        const index = context.dataIndex;
                        const item = visibleData[index];

                        if (!item) {
                            return "";
                        }

                        const porcentaje =
                            item.percentage !== undefined
                                ? ` (${item.percentage} %)`
                                : "";

                        return `${item.label}: ${item.value.toLocaleString(
                            "es-CL"
                        )}
                        ${porcentaje}`;
                    },
                },
            },

            // --------------------------------
            // VALORES SOBRE LAS BARRAS
            // --------------------------------

            datalabels: {
                display: true,
                color: "#ffffff",

                font: {
                    weight: "bold" as const,
                    size: 11,
                },

                formatter: function (value: number) {
                    return value.toLocaleString("es-CL");
                },

                anchor: "end" as const,
                align: "start" as const,
                clamp: true,
            },
        },

        scales: {

            // =================================
            // EJE X
            // =================================

            x: {
                beginAtZero: true,

                ticks: {
                    callback: function (
                        value: string | number
                    ) {
                        return Number(value).toLocaleString(
                            "es-CL"
                        );
                    },

                    font: {
                        size: 11,
                    },
                },

                grid: {
                    color: "rgba(0,0,0,0.08)",

                },
            },

            // =================================
            // EJE Y
            // =================================

            y: {
                ticks: {
                    autoSkip: false,
                    color: isDarkMode ? "#ffffff9a" : "#30353ac4",
                    font: {
                        size: 11,
                    },
                    callback: function (value: any) {
                        const index = Number(value);
                        const label =
                            visibleData[index]?.label ?? "";

                        if (label.length > 25) {
                            return (
                                label.substring(0, 25) +
                                "..."
                            );
                        }

                        return label;
                    },
                },

                grid: {
                    display: false,
                },
            },
        },
    };

    return (
        <div style={{ width: "100%", }}>

            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px", }}>
                <div style={{ position: "relative", width: "100%", }}>
                    <span
                        style={{
                            position: "absolute",
                            left: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: isDarkMode ? "#ffffff" : "#7d6c6c",
                            fontSize: "15px",
                            pointerEvents: "none",
                            zIndex: 1,
                        }}
                    >
                        <Search style={{ marginLeft: "0.5rem", color: isDarkMode ? "#ffffff" : "", }} />
                    </span>

                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) =>
                            setSearchTerm(e.target.value)
                        }
                        placeholder="Buscar dependencia..."
                        className={`w-100 ${isDarkMode ? "bg-dark text-white" : ""}`}
                        style={{
                            paddingLeft: "38px",
                            paddingRight: searchTerm
                                ? "38px"
                                : "12px",
                            height: "38px",
                            borderRadius: "6px",
                            border: "1px solid #ced4da",
                            // background: isDarkMode ? "#30353a" : "",
                            // color: "#ffffff",
                            fontSize: "14px",
                        }}
                    />

                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() =>
                                setSearchTerm("")
                            }
                            style={{
                                position: "absolute",
                                right: "8px",
                                top: "50%",
                                transform:
                                    "translateY(-50%)",
                                border: "none",
                                background: "transparent",
                                // color: isDarkMode ? "#ffffff" : "#6c757d",
                                cursor: "pointer",
                                fontSize: "18px",
                                lineHeight: 1,
                                padding: "4px",
                            }}
                            title="Limpiar búsqueda"
                        >
                            ×
                        </button>
                    )}
                </div>
            </div>

            <div style={{ fontSize: "12px", color: isDarkMode ? "#ffffff" : "", marginBottom: "8px", }}>
                {searchTerm ? (
                    <>
                        Mostrando{" "}
                        <strong>
                            {filteredData.length}
                        </strong>{" "}
                        de{" "}
                        <strong>{data.length}</strong>{" "}
                        dependencias
                    </>
                ) : (
                    <>
                        <strong>{data.length}</strong>{" "}
                        dependencias
                    </>
                )}
            </div>

            <div onScroll={handleScroll}
                style={{
                    height: `${containerHeight}px`,
                    overflowY: "auto",
                    overflowX: "hidden",
                    position: "relative",
                    // border: "1px solid #e5e5e5",
                    // borderRadius: "8px",
                    background: "#ffffff",
                    // color: isDarkMode ? "#ffffff" : "#6c757d",
                    scrollbarWidth: "thin",
                }}
            >
                <div style={{ height: `${totalHeight}px`, width: "100%", position: "relative", }}>
                    <div
                        style={{
                            position: "sticky",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: `${containerHeight}px`,
                            background: isDarkMode ? "#30353a" : "",
                            // color: isDarkMode ? "#ffffff" : "",
                            zIndex: 2,
                            overflow: "hidden",
                        }}
                    >
                        {filteredData.length > 0 ? (
                            <Bar
                                data={chartData}
                                options={barOptions}
                            />
                        ) : (
                            <div
                                style={{
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexDirection: "column",
                                    color: "#6c757d",

                                }}
                            >
                                <div
                                    style={{
                                        fontSize: "30px",
                                        marginBottom: "8px",

                                    }}
                                >
                                    <Search
                                        style={{
                                            marginLeft: "0.5rem",
                                            color: "#6c757d",
                                        }}
                                    />
                                </div>

                                <div
                                    style={{
                                        fontSize: "14px",
                                        fontWeight: 500
                                    }}
                                >
                                    No se encontraron
                                    dependencias
                                </div>

                                <small
                                    style={{
                                        marginTop: "4px",

                                    }}
                                >
                                    Intenta con otro nombre
                                </small>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div >
    );
};
const mapStateToProps = (state: RootState) => ({
    isDarkMode: state.darkModeReducer.isDarkMode,
});
export default connect(mapStateToProps, {
}
)(UbicacionBarChart);